// Kebab menu (CONTRACT "Call row" actions): a 24 px trigger and a wall-step
// menu with roving focus. The list renders through a portal at a fixed
// position computed from the trigger, flipping above when the space under the
// trigger is short, so a menu on the last visible row is never clipped by an
// overflow scroller (redo-1 P1). It closes on scroll and resize. Arrow keys
// move, Home and End jump, Escape closes and returns focus to the trigger,
// an outside click closes. Items may carry a key cap hint.
//
// Focus, the redo-2 P1: the list is hidden before it is measured with
// opacity + pointer-events, never `visibility: hidden`, because a
// visibility-hidden element cannot take focus and `focus()` silently no-ops
// on it. Placement and the first focus are two layout effects in the same
// commit, so the first menu item is focused before paint on every open.
import { useCallback, useEffect, useLayoutEffect, useId, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { DotsThreeVertical } from '@phosphor-icons/react';
import { cx } from './cx';
import { KeyCap } from './KeyCap';
import './Menu.css';

// Keeps the flipped menu clear of the 24 px status bar (CONTRACT shell).
const VIEWPORT_MARGIN = 24;
const TRIGGER_GAP = 2;

export interface MenuItem {
  readonly id: string;
  readonly label: string;
  readonly onSelect: () => void;
  readonly keyHint?: string;
  readonly disabled?: boolean;
  readonly icon?: ReactNode;
}

export interface MenuProps {
  readonly items: ReadonlyArray<MenuItem>;
  readonly label: string;
  readonly align?: 'start' | 'end';
  readonly trigger?: ReactNode;
  readonly triggerClassName?: string;
  readonly className?: string;
}

interface MenuPosition {
  readonly top: number;
  readonly left: number;
}

export function placeMenu(
  trigger: { readonly top: number; readonly bottom: number; readonly left: number; readonly right: number },
  menu: { readonly width: number; readonly height: number },
  viewport: { readonly width: number; readonly height: number },
  align: 'start' | 'end',
): MenuPosition {
  const below = trigger.bottom + TRIGGER_GAP;
  const fitsBelow = below + menu.height <= viewport.height - VIEWPORT_MARGIN;
  const above = trigger.top - TRIGGER_GAP - menu.height;
  const top = fitsBelow || above < VIEWPORT_MARGIN ? below : above;
  const raw = align === 'end' ? trigger.right - menu.width : trigger.left;
  const left = Math.min(Math.max(8, raw), Math.max(8, viewport.width - menu.width - 8));
  return { top, left };
}

export function Menu({ items, label, align = 'end', trigger, triggerClassName, className }: MenuProps) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<MenuPosition | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const enabledIndexes = items.map((item, index) => (item.disabled ? -1 : index)).filter((i) => i >= 0);

  const itemNodes = useCallback(
    (): ReadonlyArray<HTMLButtonElement> => Array.from(listRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? []),
    [],
  );

  const focusItem = useCallback((index: number) => {
    itemNodes()[index]?.focus();
  }, [itemNodes]);

  const close = useCallback((restoreFocus: boolean) => {
    setOpen(false);
    setPosition(null);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  const firstEnabled = enabledIndexes[0] ?? 0;

  // Place the portal list against the trigger before paint, re-measuring when
  // the item list changes so a taller menu still flips correctly.
  const itemCount = items.length;
  useLayoutEffect(() => {
    if (!open) return;
    const triggerEl = triggerRef.current;
    const listEl = listRef.current;
    if (!triggerEl || !listEl) return;
    const rect = triggerEl.getBoundingClientRect();
    setPosition(placeMenu(
      { top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right },
      { width: listEl.offsetWidth, height: listEl.offsetHeight },
      { width: window.innerWidth, height: window.innerHeight },
      align,
    ));
  }, [open, align, itemCount]);

  // First focus, once per open and in the same commit as the placement above
  // (layout effects run in declaration order). The list is focusable while
  // still unplaced because it is hidden with opacity, not visibility: a
  // visibility-hidden element silently refuses focus, which is what broke the
  // whole keyboard model on the row kebab (redo-2 P1).
  useLayoutEffect(() => {
    if (!open) return;
    listRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]')[firstEnabled]?.focus();
    // Deliberately not keyed on firstEnabled: re-running would pull focus back
    // to the first item while the reader is moving through the menu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || listRef.current?.contains(target)) return;
      close(false);
    };
    // A scrolled or resized page invalidates the fixed position; close.
    const onMove = (): void => close(false);
    document.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, close]);

  const currentIndex = (): number => itemNodes().findIndex((node) => node === document.activeElement);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const pos = enabledIndexes.indexOf(currentIndex());
    let next: number | undefined;
    if (event.key === 'ArrowDown') next = enabledIndexes[(pos + 1) % enabledIndexes.length];
    else if (event.key === 'ArrowUp') next = enabledIndexes[(pos - 1 + enabledIndexes.length) % enabledIndexes.length];
    else if (event.key === 'Home') next = enabledIndexes[0];
    else if (event.key === 'End') next = enabledIndexes[enabledIndexes.length - 1];
    else if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(true); return; }
    else if (event.key === 'Tab') { close(false); return; }
    if (next === undefined) return;
    event.preventDefault();
    focusItem(next);
  };

  // An open menu that still has focus on its trigger (a pointer user who
  // opened it and reached for the keyboard) stays dismissible.
  const onTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (open) focusItem(firstEnabled);
      else setOpen(true);
      return;
    }
    if (!open) return;
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(false); return; }
    if (event.key === 'Tab') close(false);
  };

  const listStyle: CSSProperties = position ? { top: position.top, left: position.left } : { top: 0, left: 0 };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={cx('kebab', triggerClassName)}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => (open ? close(false) : setOpen(true))}
        onKeyDown={onTriggerKeyDown}
      >
        {trigger ?? <DotsThreeVertical weight="bold" />}
      </button>
      {open
        ? createPortal(
            <div
              ref={listRef}
              id={menuId}
              role="menu"
              aria-label={label}
              className={cx('menu', className)}
              data-placed={position ? 'true' : 'false'}
              style={listStyle}
              onKeyDown={onKeyDown}
            >
              {items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="menuitem"
                  tabIndex={-1}
                  disabled={item.disabled}
                  className="menu-item"
                  onClick={() => { close(true); item.onSelect(); }}
                >
                  {item.icon ? <span className="menu-ic" aria-hidden="true">{item.icon}</span> : null}
                  <span className="menu-lbl">{item.label}</span>
                  {item.keyHint ? <KeyCap className="menu-kc">{item.keyHint}</KeyCap> : null}
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
